import { Copyright, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { useSignIn } from "react-auth-kit";
import bcrypt from "bcryptjs-react";
import { useNavigate } from "react-router-dom";
import localize from "../../common/localize";
import ApplicationLogoVertical from "../../components/common/ApplicationLogoVertical";
import { parseJwt } from "../../common/utils";
import {
  AUTH_ENDPOINT,
  PROFILE_ENDPOINT,
  COMPANY_ENDPOINT,
} from "../../services/config";

export default function Login() {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [userName, setUserName] = useState("");
  const [errorMessage, setErrorMessage] = useState(
    `${localize.check_credentials}`,
  );
  const signIn = useSignIn();
  const navigate = useNavigate();
  const [errorSnack, setErrorSnack] = useState();
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("");

  const assignUserName = (event) => {
    setUserName(event.target.value);
  };

  const assignPassword = (event) => {
    setPassword(event.target.value);
  };

  const throwError = (message) => {
    setErrorSnack(true);
    setErrorMessage(message);
  };

  const handleLogin = async () => {
    const hashedPassword = bcrypt.hashSync(
      password,
      process.env.REACT_APP_BCRYPT_SALT,
    );

    setLoading(true);
    setLoadingLabel(localize.login_in_progress);

    try {
      const result = await axios.post(
        `${AUTH_ENDPOINT}/authenticate`,
        {
          UserName: userName,
          Password: hashedPassword,
        },
        {
          headers: {
            ApplicationId: localize.app_name,
            pagination: 0,
          },
        },
      );

      if (result.data.ResultCode !== 0) {
        setLoading(false);
        if (result.data.ResultCode === 101) {
          throwError(localize.invalid_credentials);
        } else {
          throwError(`Server Error: ${result.data.Message}`);
        }
        return;
      }

      const parsedData = parseJwt(result.data.Data);
      let userData;
      try {
        userData = JSON.parse(parsedData.data);
      } catch (error) {
        setLoading(false);
        throwError(`${localize.error_unexpected}: USER DATA BAD FORMED`);
        return;
      }

      const token = result.data.Data;
      const authRequestConfig = {
        headers: {
          authenticate: `bearer ${token}`,
          ApplicationId: localize.app_name,
          pagination: 0,
          ManagementId: userData?.ManagementId,
        },
      };

      // Obtain the user permissions (restrictions) separately and store them
      // in UserData.UserRoles. Permissions may be null/empty and that is Ok.
      setLoadingLabel(localize.getting_permissions);
      try {
        const roleResult = await axios.get(
          `${PROFILE_ENDPOINT}/user/${userData?.UserId}`,
          authRequestConfig,
        );
        const permissions = roleResult?.data?.Data ?? roleResult?.data;
        userData.UserRoles = Array.isArray(permissions)
          ? permissions
          : permissions
            ? [permissions]
            : [];
      } catch (error) {
        // Permissions call failed: clear the session data and show the form.
        userData = null;
        setLoading(false);
        throwError(`${localize.error_unexpected}: ${error.message}`);
        return;
      }

      // Obtain the company data. It may be null/empty and that is Ok.
      setLoadingLabel(localize.getting_company_info);
      try {
        await axios.get(`${COMPANY_ENDPOINT}`, authRequestConfig);
      } catch (error) {
        // Company data is optional; continue to the home page regardless.
      }

      try {
        signIn({
          token,
          tokenType: "bearer",
          authState: userData,
          expiresIn: 1440,
        });
        navigate("/");
      } catch (error) {
        setLoading(false);
        throwError(`${localize.error_unexpected}: USER DATA BAD FORMED`);
      }
    } catch (error) {
      setLoading(false);
      throwError(`${localize.error_unexpected}: ${error.message}`);
    }
  };

  const keyPressValidation = (event) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div className="flex-col items-center">
      <Grid
        container
        component="main"
        elevation={10}
        style={{ height: "100vh" }}
      >
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={errorSnack}
          autoHideDuration={2500}
          onClose={() => {
            setErrorSnack(false);
          }}
        >
          <Alert severity="error" sx={{ width: "100%" }}>
            <label className="text-xs">{errorMessage}</label>
          </Alert>
        </Snackbar>
        <CssBaseline />

        <div
          className="flex items-center justify-center w-full"
          style={{ backgroundColor: "#DDDEDD" }}
        >
          <div
            className="flex"
            style={{
              width: "100%",
              maxWidth: "900px",
            }}
          >
            <Grid
              item
              xs={false}
              lg={5}
              sx={{
                display: { xs: "none", lg: "block" }, // 👈 OCULTAR EN MÓVIL Y TABLETS
                borderRadius: "16px 0px 0px 16px",
                padding: "50px",
                backgroundImage: `linear-gradient(
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.1)
    ),
    url(${process.env.PUBLIC_URL}/img/login_photo_3.PNG)`,
                backgroundRepeat: "no-repeat",
                backgroundColor: (t) =>
                  t.palette.mode === "light"
                    ? t.palette.grey[50]
                    : t.palette.grey[900],
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box className="flex flex-col w-full h-full justify-center gap-4">
                <Typography sx={{ color: "white", fontSize: "20px" }}>
                  ✔ Gestione servicios
                </Typography>
                <Typography sx={{ color: "white", fontSize: "20px" }}>
                  ✔ Valide su ejecución
                </Typography>
                <Typography sx={{ color: "white", fontSize: "20px" }}>
                  ✔ Documente la operación
                </Typography>
                <Typography sx={{ color: "white", fontSize: "20px" }}>
                  ✔ Administre los recursos
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ color: "white", marginTop: "30px" }}
                >
                  No controle, <b>Sólo Koordine</b>
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              lg={7}
              component={Paper}
              sx={{
                borderRadius: {
                  xs: "16px",
                  lg: "0px 16px 16px 0px",
                },
              }}
            >
              <Box
                sx={{
                  my: 8,
                  mx: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <ApplicationLogoVertical height={150} width={150} />
                <div className="mb-5" />
                <Box component="form" sx={{ mt: 1 }}>
                  {loading ? (
                    <Box
                      className="flex flex-col items-center justify-center"
                      sx={{ py: 8, gap: 2 }}
                    >
                      <CircularProgress sx={{ color: "#10366E" }} />
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          color: "#10366E",
                          textAlign: "center",
                        }}
                      >
                        {loadingLabel}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="username"
                        onChange={assignUserName}
                        label={localize.username}
                        autoFocus
                        onKeyUp={keyPressValidation}
                      />
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label={localize.password}
                        onChange={assignPassword}
                        type={showPassword ? "text" : "password"}
                        id="password"
                        onKeyUp={keyPressValidation}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip
                                title={
                                  showPassword
                                    ? localize.hide_password ?? "Ocultar"
                                    : localize.show_password ?? "Mostrar"
                                }
                              >
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onMouseEnter={() => setShowPassword(true)}
                                  onMouseLeave={() => setShowPassword(false)}
                                  onFocus={() => setShowPassword(true)}
                                  onBlur={() => setShowPassword(false)}
                                  onClick={() =>
                                    setShowPassword((prev) => !prev)
                                  }
                                  tabIndex={-1}
                                >
                                  {showPassword ? (
                                    <VisibilityOff fontSize="small" />
                                  ) : (
                                    <Visibility fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button
                        sx={{
                          fontSize: "0.8em",
                          mt: 3,
                          mb: 2,
                          backgroundColor: "#10366E",
                        }}
                        size="large"
                        variant="contained"
                        fullWidth
                        disabled={userName.length <= 0 || password.length <= 0}
                        onClick={handleLogin}
                        className="btn-success app_general_button_data"
                      >
                        {localize.enter}
                      </Button>
                    </>
                  )}
                  <div className="flex justify-center" style={{ gap: "10px" }}>
                    <Copyright />
                    {`${localize.app_name} ${
                      localize.version
                    } - ${new Date().getFullYear()}`}{" "}
                  </div>
                </Box>
              </Box>
            </Grid>
          </div>
        </div>
      </Grid>
    </div>
  );
}
